import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { DishCustomize, DishHeading, DishOrderControls } from "./dish-detail-parts"
import { DishIllustration } from "./dish-illustration"
import { useDishOrder } from "./use-dish-order"
import { usePickupHours } from "./use-pickup-hours"
import { cn } from "@/lib/utils"
import type { MenuItem, ServiceContext } from "./types"
import { t } from "@/lib/i18n"

type DishDetailDrawerProps = {
  item: MenuItem | null
  billId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
  serviceContext?: ServiceContext
  /** The pre-seating menu: the dish is readable, but nothing here can order it. */
  readOnly?: boolean
}

export function DishDetailDrawer({
  item,
  billId,
  open,
  onOpenChange,
  serviceContext = "dine_in",
  readOnly = false,
}: DishDetailDrawerProps) {
  return (
    <ResponsiveDrawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        data-testid="dish-detail-drawer"
        className="mx-auto w-full data-[vaul-drawer-direction=bottom]:max-w-lg data-[vaul-drawer-direction=right]:sm:max-w-md"
      >
        {item && (
          <DishDetailDrawerInner
            item={item}
            billId={billId}
            serviceContext={serviceContext}
            readOnly={readOnly}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DrawerContent>
    </ResponsiveDrawer>
  )
}

function DishDetailDrawerInner({
  item,
  billId,
  serviceContext,
  readOnly,
  onClose,
}: {
  item: MenuItem
  billId: number | null
  serviceContext: ServiceContext
  readOnly: boolean
  onClose: () => void
}) {
  const isPickup = serviceContext === "pickup"
  const order = useDishOrder(item, isPickup ? "pick_up" : "eat_in", { readOnly })
  const { closed: pickupClosed } = usePickupHours()
  const hasPhoto = Boolean(item.imageUrl)
  const unavailable = item.available === false

  return (
    <>
      {/* The deep-link page's order, in a sheet: photo, then name, price and
          description together, then the customisation. The two used to arrange
          this differently — the price rode in as a `DrawerDescription`, which
          `DrawerHeader`'s bottom-direction `text-center` then pulled off the
          title it belongs under. `DishHeading` is now the single source for the
          block, so the drawer and the page cannot drift again.

          `DrawerHeader` is kept for the close button alone, so the sheet still
          has a header row to anchor it to and the photo can start at the top. */}
      <DrawerHeader className="relative p-0">
        <DrawerClose asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "absolute right-2 top-2 z-10 rounded-full",
              // Over a photo it needs its own surface to stay legible against
              // whatever the picture happens to be behind it.
              hasPhoto && "bg-background/90 backdrop-blur-sm hover:bg-background"
            )}
            aria-label={t("ui.common.actions.close")}
          >
            <X />
          </Button>
        </DrawerClose>
      </DrawerHeader>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {hasPhoto && (
          <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
            <DishIllustration src={item.imageUrl} alt={item.name} />
          </div>
        )}

        <div className="px-4 pb-4 pt-4">
          <DishHeading
            name={item.name}
            emoji={item.emoji}
            showEmoji={!hasPhoto}
            price={order.basePrice}
            originalPrice={order.originalPrice}
            promotionName={order.promotion?.name}
            currency={item.currency}
            description={item.description}
            // Radix still needs a labelled title, and a described dialog when
            // there is a description to point at.
            titleComponent={DrawerTitle}
            descriptionComponent={item.description ? DrawerDescription : undefined}
            // Clears the floating close button when there is no photo to put it on.
            className={hasPhoto ? undefined : "pr-10"}
          />

          <div className="mt-6">
            <DishCustomize
              groups={order.groups}
              currency={item.currency}
              selectedIds={order.selectedIds}
              toggleExtra={order.toggleExtra}
              clearGroup={order.clearGroup}
              note={order.note}
              setNote={order.setNote}
              serviceType={isPickup ? undefined : order.serviceType}
              onServiceTypeChange={isPickup ? undefined : order.setServiceType}
            />
          </div>
        </div>
      </div>

      {(unavailable || !readOnly) && (
        <DrawerFooter className="border-t pb-[calc(env(safe-area-inset-bottom)+1rem)]">
          {unavailable && (
            <p
              data-testid="dish-unavailable-notice"
              className="rounded-lg bg-muted px-3 py-2 text-center text-xs font-medium text-muted-foreground"
            >
              {t("ui.user.dish.unavailable_reason")}
            </p>
          )}
          {!readOnly && (
            <DishOrderControls
              qty={order.qty}
              setQty={order.setQty}
              total={order.total}
              currency={item.currency}
              blockedReason={
                // Closed first: it is the reason the diner can do something about,
                // and on a pickup screen there is no table to lack.
                pickupClosed ? "closed" : !order.usesLocalCart && billId === null ? "no_table" : null
              }
              submitting={order.submitting}
              canSubmit={order.canSubmit}
              unavailable={unavailable}
              onAdd={() => order.submit({ onSuccess: onClose })}
            />
          )}
        </DrawerFooter>
      )}
    </>
  )
}
